import { useCallback, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Eye, EyeOff } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { AnimatedLoginButton } from "@/components/animated-login-button"
import { SignupRoleCards } from "@/components/auth/signup-role-cards"
import { useAuth } from "@/hooks/use-auth"
import { CURRENT_CLIENT } from "@/constants"
import { SignupLegalNotice } from "@/components/auth/auth-legal-links"
import { AuthBackToSignInLink } from "@/components/auth/auth-back-to-sign-in-link"
import { AuthDivider } from "@/components/auth/auth-divider"
import { SocialAuthButtons } from "@/components/auth/social-auth-buttons"
import { socialAuthEnabled } from "@/constants/auth-social"
import { getSignupConfig, getSignupRoles, signupRolesRequired } from "@/constants/signup"
import { CaptchaField, type CaptchaFieldHandle } from "@/components/auth/captcha-field"
import { captchaRequiredForAction } from "@/lib/captcha"
import { useCaptchaConfig } from "@/hooks/use-captcha-config"
import { clientCaptchaActionExpected } from "@/constants/captcha"

const CAPTCHA_LOAD_ERROR =
  "Could not load the security check. Refresh the page or try again in a moment."

interface SignupFormProps extends React.ComponentPropsWithoutRef<"div"> {
  onSuccess?: () => void;
}

export function SignupForm({ className, onSuccess, ...props }: SignupFormProps) {
  const signupRoles = useMemo(() => getSignupRoles(), [])
  const signupHeading = getSignupConfig().heading ?? "Which describes you best?"
  const rolesRequired = signupRolesRequired()

  const [signupRole, setSignupRole] = useState<string | null>(
    signupRoles.length === 1 ? signupRoles[0].id : null,
  )
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaWidgetReady, setCaptchaWidgetReady] = useState(false)
  const [captchaPending, setCaptchaPending] = useState(false)
  const { config: captchaConfig, loaded: captchaConfigLoaded, fetchFailed: captchaFetchFailed } =
    useCaptchaConfig()
  const captchaExpected = clientCaptchaActionExpected("registration")
  const captchaUnavailable = captchaConfigLoaded && captchaFetchFailed && captchaExpected
  const captchaRequired = captchaRequiredForAction(captchaConfig, "registration")
  const captchaInvisible = captchaConfig?.widget?.size === "invisible"
  const captchaRef = useRef<CaptchaFieldHandle>(null)
  const pendingCaptchaSubmitRef = useRef(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const jobInviteToken = searchParams.get("job_invite")?.trim() || undefined
  const { register, isLoading } = useAuth()

  const canSubmit =
    captchaConfigLoaded &&
    !captchaUnavailable &&
    Boolean(email && password) &&
    (!rolesRequired || Boolean(signupRole)) &&
    (!captchaRequired || captchaInvisible || Boolean(captchaToken))

  const submitRegistration = useCallback(
    async (token?: string | null) => {
      const data = await register(email, password, {
        signupRole: signupRole ?? undefined,
        captchaToken: token ?? undefined,
        jobInviteToken,
      })

      if (!data.success) {
        setError(data.error || "Failed to create account")
        captchaRef.current?.reset()
        return
      }

      if (data.email_verification_required) {
        navigate("/register/check-email", { state: { email } })
        onSuccess?.()
        return
      }

      if (data.approval_pending) {
        navigate("/register/pending", { state: { email } })
        onSuccess?.()
        return
      }

      if (data.access_token) {
        if (data.onboarding_redirect) {
          navigate("/onboarding")
        } else {
          navigate("/profile")
        }
        onSuccess?.()
        return
      }

      navigate("/login", {
        state: {
          message: "Account created successfully! Please log in.",
        },
      })
      onSuccess?.()
    },
    [email, password, register, signupRole, navigate, onSuccess, jobInviteToken],
  )

  const handleCaptchaTokenChange = useCallback(
    (token: string | null) => {
      setCaptchaToken(token)
      if (token && pendingCaptchaSubmitRef.current) {
        pendingCaptchaSubmitRef.current = false
        setCaptchaPending(false)
        void submitRegistration(token)
      }
    },
    [submitRegistration],
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!email || !password) {
      setError("Please fill in all fields")
      return
    }

    if (rolesRequired && !signupRole) {
      setError("Please select an account type")
      return
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long")
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address")
      return
    }

    if (captchaUnavailable) {
      setError(CAPTCHA_LOAD_ERROR)
      return
    }

    if (captchaRequired && captchaInvisible && !captchaToken) {
      if (!captchaWidgetReady) {
        setError("Security check is still loading. Please wait a moment and try again.")
        return
      }
      pendingCaptchaSubmitRef.current = true
      setCaptchaPending(true)
      const started = captchaRef.current?.execute()
      if (started === false) {
        pendingCaptchaSubmitRef.current = false
        setCaptchaPending(false)
        setError("Security check is still loading. Please wait a moment and try again.")
      }
      return
    }

    if (captchaRequired && !captchaInvisible && !captchaToken) {
      setError("Please complete the captcha verification")
      return
    }

    await submitRegistration(captchaToken)
  }

  const busy = isLoading || captchaPending

  return (
    <div className={cn("flex flex-col gap-6 lg:gap-4", className)} {...props}>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6 lg:gap-4">
          {(error || captchaUnavailable) && (
            <div
              className="p-4 text-sm text-destructive border border-destructive/20 rounded-md bg-destructive/10"
              role="alert"
            >
              {error || CAPTCHA_LOAD_ERROR}
            </div>
          )}

          {signupRoles.length > 0 ? (
            <div className="grid gap-3 lg:gap-2">
              <p className="text-sm font-medium text-foreground">{signupHeading}</p>
              <SignupRoleCards
                roles={signupRoles}
                value={signupRole}
                onChange={setSignupRole}
                disabled={busy}
              />
            </div>
          ) : null}

          {socialAuthEnabled() ? (
            <>
              <SocialAuthButtons signupRole={signupRole} disabled={busy} />
              <AuthDivider />
            </>
          ) : null}

          <div className="grid gap-2">
            <Label htmlFor="signup-email">Email</Label>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              placeholder={CURRENT_CLIENT.placeholderEmail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={busy}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="signup-password">Password</Label>
            <div className="relative">
              <Input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={CURRENT_CLIENT.placeholderPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={busy}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={busy}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Use at least 8 characters with a mix of letters and numbers.
            </p>
          </div>
          {captchaRequired && !captchaInvisible ? (
            <p className="text-sm font-medium text-foreground">Verify you are human</p>
          ) : null}
          <CaptchaField
            ref={captchaRef}
            action="registration"
            disabled={busy}
            onReady={() => setCaptchaWidgetReady(true)}
            onTokenChange={handleCaptchaTokenChange}
            onMisconfigured={() => {
              setError("Sign-up is temporarily unavailable. Please contact support.")
            }}
            onExecuteError={() => {
              pendingCaptchaSubmitRef.current = false
              setCaptchaPending(false)
              setError("Captcha verification failed. Please try again.")
            }}
          />
          <AnimatedLoginButton
            disabled={busy || !canSubmit}
            isLoading={busy}
            mode="signup"
          />
          <AuthBackToSignInLink disabled={busy} />
          <SignupLegalNotice />
        </div>
      </form>
    </div>
  )
}
